<template>
	<div class="view-base" v-bind:class="[...className]"></div>
</template>

<script lang="ts">
	import * as Config from '@/Config';
	import ChalkySticks from '@chalkysticks/sdk';
	import { Component, Vue } from 'vue-property-decorator';
	import { created, mounted } from '@/Utility/Decorators';

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package Core
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewBase extends Vue {
		/**
		 * @type ChalkySticks
		 */
		public get ChalkySticks(): typeof ChalkySticks {
			return ChalkySticks;
		}

		/**
		 * @type string
		 */
		public cid: string = Math.random().toString(32).substr(2, 7);

		/**
		 * Class names we can apply to the element
		 *
		 * @type string[]
		 */
		public className: string[] = [];

		/**
		 * @type Record<string, any>
		 */
		public config: Record<string, any> = Config;

		/**
		 * @type number
		 */
		public mounted: boolean = false;

		/**
		 * Unique symbol for every class to reference
		 *
		 * @type symbol
		 */
		public symbol: symbol = Symbol();

		/**
		 * Automatically bind functions to scope
		 *
		 * @type string[]
		 */
		protected bindings: string[] = [];

		/**
		 * General delta for increasing values
		 *
		 * @type number
		 */
		protected delta: number = 0;

		/**
		 * Bind associated methods with class
		 *
		 * @type string[]
		 */
		@created
		public setupBindings(): void {
			let binding: string;

			for (binding of this.bindings) {
				// @ts-ignore
				this[binding] = this[binding].bind(this);
			}
		}

		/**
		 * @param string key
		 * @return string
		 */
		public getQueryParameter(key: string): string {
			return this.$route?.query[key] as string;
		}

		/**
		 * @param string key
		 * @param string value
		 * @return void
		 */
		public setQueryParameter(key: string, value: string): void {
			const oScrollY: number = window.scrollY;

			// Navigate to tab
			this.$router.push({
				query: Object.assign({ ...this.$route.query }, { [key]: value }),
			});

			// ScrollY
			window.scrollTo(0, oScrollY);
		}

		/**
		 * @param string key
		 * @return void
		 */
		public removeQueryParameter(paramName: string): string {
			// Create a URL object from the current window location
			const url = new URL(window.location.href);

			// Get the URLSearchParams object
			const searchParams = url.searchParams;

			// Delete the specified parameter
			searchParams.delete(paramName);

			// Convert the search parameters back to a string
			const newSearch = searchParams.toString();

			// Create the new URL string
			const newUrl = `${url.origin}${url.pathname}${newSearch ? `?${newSearch}` : ''}${url.hash}`;

			// Update the browser history without reloading the page
			window.history.pushState({ path: newUrl }, '', newUrl);

			// Return the new URL
			return newUrl;
		}

		/**
		 * @param string methodName
		 * @return void
		 */
		protected bind(methodName: string): void {
			// @ts-ignore
			this[methodName] = this[methodName].bind(this);
		}

		/**
		 * Add CID to DOM element for easy access
		 *
		 * @return void
		 */
		@mounted
		private attachCid(): void {
			this.$el['setAttribute'] && this.$el.setAttribute('data-cid', this.cid);
		}

		/**
		 * Add CID to DOM element for easy access
		 *
		 * @return void
		 */
		@mounted
		private isMounted(): void {
			this.mounted = true;
		}
	}
</script>
